import { useMemo } from "react"
import { z } from "zod"
import { useAuth0 } from "@auth0/auth0-react"

import { useSafeLocalStorage } from "~/hooks/useSafeLocalStorage"
import { type PresetView } from "~/components/planning/presetView"
import { type SavedTableView } from "~/server/api/routers/tableView"
import { resolveViewTarget, type ViewTarget } from "~/hooks/useViewRoute"

/**
 * A user's chosen default view for a given `tableId`. Persisted client-side only (localStorage) —
 * there is no backend for this feature.
 */
export type DefaultViewTarget = ViewTarget

const defaultViewTargetSchema: z.ZodType<DefaultViewTarget> = z.union([
  z.object({ viewId: z.number() }).strict(),
  z.object({ presetId: z.string() }).strict(),
  z.null(),
])

/** Resolve a stored default target against the views/presets currently available to the user. */
export function resolveDefaultTarget({
  target,
  finalViews,
  presets,
}: {
  target: DefaultViewTarget
  finalViews: SavedTableView[]
  presets: PresetView[]
}): { kind: "view"; view: SavedTableView } | { kind: "preset"; preset: PresetView } | null {
  return resolveViewTarget({ target, finalViews, presets })
}

/**
 * Client-only default-view preference for a page/tab, unique per org + user + scope
 * (localStorage key `default-view.<userId>.<scopeKey>`). The caller passes a stable scope key —
 * the preset group id where one exists, else the tableId — so multi-preset tabs share a single
 * default across their presets.
 */
export function useDefaultView(scopeKey: string | undefined) {
  const { user } = useAuth0()
  const userId = user?.sub ?? ""

  const [defaultTarget, setDefaultTarget] = useSafeLocalStorage<DefaultViewTarget>(
    `default-view.${userId}.${scopeKey ?? ""}`,
    null,
    defaultViewTargetSchema,
  )

  return useMemo(
    () => ({
      defaultTarget,
      setViewDefault: (viewId: number) => setDefaultTarget({ viewId }),
      setPresetDefault: (presetId: string) => setDefaultTarget({ presetId }),
      unsetDefault: () => setDefaultTarget(null),
      isViewDefault: (viewId: number | undefined) =>
        viewId !== undefined && !!defaultTarget && "viewId" in defaultTarget
          ? defaultTarget.viewId === viewId
          : false,
      isPresetDefault: (presetId: string) =>
        !!defaultTarget && "presetId" in defaultTarget && defaultTarget.presetId === presetId,
      // No custom default set → the system default is effectively the user's default.
      isSystemDefault: defaultTarget === null,
    }),
    [defaultTarget, setDefaultTarget],
  )
}
